import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { Drawer, Button, notification } from 'antd';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../../api/useAPIMutation';
import Form from '../../components/Form';
import PageTitle from '../../components/PageTitle';
import StaffUserForm, { StaffUserFormValues } from '../../components/StaffUserForm';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import styles from './CreateUser.module.css';

const StaffCreateUser = () => {
  const { t } = useTranslation();

  const [form] = Form.useForm<StaffUserFormValues>();
  const queryClient = useQueryClient();

  const { tenant } = useTypedParams({ tenant: String });
  const navigate = useTypedNavigate();

  const mutation = useAPIMutation(
    async (variables: StaffUserFormValues, api) => {
      const user = await api.post(`/staff-users/`, {
        username: variables.username,
        full_name: variables.name,
        email: variables.email,
      });

      return user;
    },
    {
      onSuccess: (response) => {
        notification.success({
          message: t('Usuário criado com sucesso!'),
          description: t(
            'Enviamos um email para o novo usuário com as instruções de primeiro acesso.'
          ),
          duration: 0,
        });
        navigate({
          pathname: '/:tenant/settings/cs-staff/staff-users/:user_id/',
          params: { tenant, user_id: response.data.id },
        });
      },
      onError: (err, variables, context) => {
        // TODO: pog temporario arrumar dps
        if (
          err?.response?.data?.email?.[0] === 'usuário com este Endereço de e-mail já existe.' ||
          err?.response?.data?.email?.[0] === 'user with this Email address already exists.'
        ) {
          return notification.error({
            duration: 0,
            message: t('Atenção'),
            description: t('Já existe um usuário com este endereço de E-mail cadastrado.'),
          });
        }
        if (
          err?.response?.data?.username?.[0] === 'Um usuário com este nome de usuário já existe.' ||
          err?.response?.data?.username?.[0] === 'A user with that username already exists.' // TODO: POG TEMPORARIO ARRUMAR DPS
        ) {
          return notification.error({
            duration: 0,
            message: t('Atenção'),
            description: t('Já existe uma pessoa com esse nome de Usuário cadastrado.'),
          });
        }

        return queryClient.defaultMutationOptions().onError?.(err, variables, context);
      },
      onSettled: () => {
        queryClient.invalidateQueries('/staff-users/');
      },
    }
  );

  const onClose = () =>
    navigate({ pathname: '/:tenant/settings/cs-staff/staff-users/', params: { tenant } });

  return (
    <Drawer
      className={styles.drawer}
      title={
        <PageTitle
          title={t('Novo usuário staff')}
          level={2}
          extra={[
            <Button key="close" type="text" onClick={onClose} shape="circle">
              <CloseOutlined />
            </Button>,
          ]}
        />
      }
      maskClosable
      size="large"
      closable={false}
      width={720}
      footer={
        <Button
          type="primary"
          htmlType="submit"
          icon={<PlusOutlined />}
          loading={mutation.isLoading}
          onClick={() => form.submit()}
        >
          {' '}
          <Trans>Adicionar</Trans>
        </Button>
      }
      open
      onClose={onClose}
    >
      <Helmet>
        <title>
          {t('Novo usuário staff')} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <StaffUserForm
        form={form}
        layout="vertical"
        onFinish={(variables) => {
          mutation.mutate(variables);
        }}
      />
    </Drawer>
  );
};

export default withRouteChange()(StaffCreateUser);
